import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  fetchSubscription,
  fetchEntitlements,
  type Entitlements,
  startTrial,
  Subscription,
} from '../api/billingApi';
import { cached, cacheInvalidate } from '../api/apiCache';
import { useAuth } from './AuthContext';

interface BillingContextValue {
  subscription: Subscription | null | undefined;
  loading: boolean;
  trialDaysRemaining: number | null;
  isTrialing: boolean;
  isActive: boolean;
  isExpired: boolean;
  isCancelled: boolean;
  canUse: (feature: string) => boolean;
  /** Server-provided plan contents; undefined until loaded, null when unavailable. */
  entitlements: Entitlements | null | undefined;
  /** The plan a locked feature needs, or null when allowed/unknown. */
  upgradeTierFor: (feature: string) => string | null;
  /** Navigation entry → required feature (server-defined). */
  navigationFeature: (entry: string) => string | null;
  /** The subscription lookup failed; entitlements are unknown, not absent. */
  loadFailed: boolean;
  refresh: () => Promise<void>;
  ensureTrial: () => Promise<void>;
}

export const BillingContext = createContext<BillingContextValue | null>(null);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  // True when the subscription lookup FAILED (as opposed to returning 404 =
  // genuinely no subscription). Consumers must not treat this as 'free tier'.
  const [loadFailed, setLoadFailed] = useState(false);
  const [entitlements, setEntitlements] = useState<Entitlements | null | undefined>(undefined);

  const load = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    setLoading(true);
    if (!user) { setSubscription(null); setEntitlements(undefined); setLoading(false); return; }
    // Admins don't have billing subscriptions — skip the fetch
    try {
      const meta = JSON.parse(localStorage.getItem('auth_meta') || '{}');
      if (meta.is_admin) { setSubscription(null); setLoading(false); return; }
    } catch { /* ignore parse errors */ }
    try {
      const sub = await cached('billing-subscription', 300_000, fetchSubscription);
      setSubscription(sub);
      setLoadFailed(false);
      try {
        setEntitlements(await cached('billing-entitlements', 300_000, fetchEntitlements));
      } catch {
        setEntitlements(null); // fall back to the local table below
      }
    } catch (err) {
      // Only a 404 means "this user genuinely has no subscription". Treating any
      // other failure (401 refresh race, 5xx, network drop) as null told the app
      // the customer was unsubscribed: BillingPage redirected them to /pricing
      // and every canUse() gate closed. Keep those distinguishable.
      const status = (err as { status?: number } | null)?.status;
      if (status === 404) {
        setSubscription(null);
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { load(); }, [load]);

  const ensureTrial = useCallback(async () => {
    if (subscription) return;
    // Only auto-start a trial if the user explicitly selected a plan
    const storedPlan = localStorage.getItem('selected_trial_plan');
    if (!storedPlan) return;
    try {
      const sub = await startTrial(storedPlan);
      localStorage.removeItem('selected_trial_plan');
      setSubscription(sub);
    } catch { /* already has one */ }
  }, [subscription]);

  // Simple optimistic feature check based on subscription status/tier
  const canUse = useCallback((feature: string): boolean => {
    // The server is the authority (SRS #139/#140). The table below is only a
    // fallback for when the entitlements call itself failed, and it refuses
    // unknown features the same way the server does.
    if (entitlements) return entitlements.features[feature]?.allowed ?? false;
    if (!subscription) return false;
    const { status } = subscription;
    if (status === 'trialing') {
      const trialBlocked = ['api_access', 'team_collaboration', 'sso', 'predictive_analytics', 'dedicated_support', 'advanced_ai'];
      return !trialBlocked.includes(feature);
    }
    if (status !== 'active') return false;
    const tier = subscription.plan_tier;
    const tierMap: Record<string, string[]> = {
      basic_search: ['academic', 'startup', 'professional', 'business', 'growth', 'law_firm_individual', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      advanced_ai: ['business', 'growth', 'law_firm_individual', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      api_access: ['business', 'growth', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      team_collaboration: ['business', 'growth', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      multi_jurisdiction: ['professional', 'business', 'growth', 'law_firm_individual', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      export_reporting: ['professional', 'business', 'growth', 'law_firm_individual', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      custom_alerts: ['startup', 'professional', 'business', 'growth', 'law_firm_individual', 'law_firm_team', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      sso: ['business', 'growth', 'law_firm_firm', 'government_state', 'government_federal', 'enterprise'],
      predictive_analytics: ['growth', 'government_state', 'government_federal', 'enterprise'],
      dedicated_support: ['growth', 'government_state', 'government_federal', 'enterprise'],
    };
    return tierMap[feature]?.includes(tier) ?? false;
  }, [subscription, entitlements]);

  const upgradeTierFor = useCallback((feature: string): string | null => {
    if (!entitlements) return null;
    const f = entitlements.features[feature];
    return f && !f.allowed ? f.upgrade_to : null;
  }, [entitlements]);

  const navigationFeature = useCallback((entry: string): string | null => {
    return entitlements?.navigation?.[entry] ?? null;
  }, [entitlements]);

  const trialDaysRemaining = subscription?.trial_days_remaining ?? null;
  const isTrialing = subscription?.status === 'trialing';
  const isActive = subscription?.status === 'active';
  const isExpired = subscription?.status === 'expired';
  const isCancelled = subscription?.status === 'cancelled';

  return (
    <BillingContext.Provider value={{
      subscription,
      loading,
      trialDaysRemaining,
      isTrialing,
      isActive,
      isExpired,
      isCancelled,
      canUse,
      entitlements,
      upgradeTierFor,
      navigationFeature,
      loadFailed,
      refresh: async () => { cacheInvalidate('billing-subscription'); cacheInvalidate('billing-entitlements'); await load(); },
      ensureTrial,
    }}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = (): BillingContextValue => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be used inside BillingProvider');
  return ctx;
};

/**
 * Billing when there is a provider, null when there is not.
 *
 * For things that render in places which do not mount one — the shared
 * sidebar, the admin preview, tests — where throwing would take the whole page
 * down over a menu. Callers treat null as "cannot price this", and fail open.
 */
export const useOptionalBilling = (): BillingContextValue | null => useContext(BillingContext) ?? null;
